<template>
  <div>
    <form @submit="submitSearchForm">
      <div class="form-group">
        <i
          class="icon_pin_alt"
          @click="focusElement('locations-input');"
        />
        <div
          id="locations-dropdown"
          class="dropdown-menu locations-dropdown"
          :class="{show: showDropdown}"
          aria-labelledby="locationsInput"
        >
          <p class="locations-dropdown-text">
            {{ $t('lang.search.select_location') }}
          </p>

          <b-link
            href="#"
            class="dropdown-item"
            @click="locateMe"
          >
            {{ $t('lang.search.my_location') }}
          </b-link>
          <b-link
            v-for="city in citiesList"
            :key="city.name"
            href="#"
            class="dropdown-item"
            @click="setPlace(city, 'fromCitiesList')"
          >
            {{ city.name }}
          </b-link>
        </div>

        <gmap-autocomplete
          id="locations-input"
          :select-first-on-enter="true"
          class="form-control"
          :placeholder="$t('lang.search.where')"
          :value="searchAddress"
          autocomplete="off"
          required
          :options="autocompleteOptions"
          data-toggle="dropdown"
          @place_changed="setPlace"
          @input="writing"
          @focus="focusElement('locations-input')"
          @blur="closeDropdown"
        />

        <div v-if="currentLocationError">
          {{ $t('lang.search.error_location_' + currentLocationError ) }}
        </div>

        <div v-if="gettingCurrentLocation">
          {{ $t('lang.search.searching_location') }}
        </div>
      </div>

      <div class="form-group margin_bottom_10">
        <input
          id="start_date"
          v-model="inputDate"
          aria-label="Fecha"
          class="form-control"
          type="text"
          readonly
          style="padding-right: 40px; background-color: white"
          autocomplete="off"
          required
          :placeholder="$t('lang.search.dates.input_placeholder')"
          data-toggle="dropdown"
          @click="$bvModal.show('date-modal');"
        >
        <i
          class="icon-calendar"
          @click="$bvModal.show('date-modal');"
        />
      </div>

      <div class="form-group input-plus-minus margin_bottom_10">
        <input
          id="bagsNumber"
          :value="bagsLabel"
          aria-label="Cantidad de Maletas"
          class="form-control"
          type="text"
          style="padding-right: 40px;"
          pattern="\d*"
          required
          readonly
        >
        <div class="d-flex align-items-center">
          <button
            type="button"
            aria-label="menos"
            @click="bagsNumber('minus')"
          >
            <span class="icon-minus-circled" />
          </button>
          <button
            type="button"
            aria-label="más"
            @click="bagsNumber('plus')"
          >
            <span class="icon-plus-circled" />
          </button>
        </div>

        <i
          class="icon-suitcase"
          @click="focusElement('bagsNumber')"
        />
      </div>

      <Errors :errors="errors" />
      <input
        type="submit"
        :value="$t('lang.forms.search')"
      >
    </form>
    <DatePickerModal @datesSelected="updateDates" />
  </div>
</template>

<script>
import getCountryFromIp from '@/components/CommonScripts/IPInfo';

import { cities } from '@/data/cities';
import DatePickerModal from '@/components/Search/DatePickerModal';
import Errors from '../Alerts/Errors';

export default {
  name: 'SearchForm',

  components: {
    DatePickerModal,
    Errors,
  },

  props: {
    setLocation: {
      type: Object,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      searchAddress: '',
      showDropdown: false,
      inputDate: '',

      searchParams: {
        place: null,
        input_start_date: '',
        input_end_date: '',
        bags_number: 2,
      },

      errors: {},

      location: null,
      gettingCurrentLocation: false,
      currentLocationError: null,
      citiesList: [],
      autocompleteOptions: { fields: ['name', 'geometry.location', 'place_id', 'formatted_address'] },
    };
  },
  computed: {
    bagsLabel() {
      const bagsNumber = this.searchParams.bags_number;
      return `${bagsNumber} ${this.$tc('lang.search.bags_items', bagsNumber)}`;
    },
  },

  watch: {
    setLocation() {
      this.setPlace(this.setLocation);
    },
  },

  async mounted() {
    // If URL is /search/city_id. The location is set to that city.
    if (this.$route.name.includes('Search') && this.$route.params.city_id) {
      const cityId = this.$route.params.city_id;

      try {
        const { location } = cities.find((data) => data.id === cityId).geometry;
        this.searchParams.place = {
          geometry: {
            location,
          },
          formatted_address: this.$t(`lang.cities.${cityId}`),
        };
        this.searchAddress = this.$t(`lang.cities.${cityId}`);
      } catch (e) {
        await this.locateMe();
      }
      // Delete de city id from url because, after searching stores url is updated in SearchStores to parameters (x, y, start_date ...)
      this.$router.push(`/${this.$store.state.main.lang}/search`);

      await this.submitSearchForm();
    } else if (this.$route.name.includes('Search') && this.$store.state.search.searchParams === null) {
      if (this.$route.query.lat && this.$route.query.lng && !this.$route.query.start_date) {
        // has url only with coordinates - for external sellers
        this.searchParams.place = {
          geometry: {
            location: {
              lat: parseFloat(this.$route.query.lat),
              lng: parseFloat(this.$route.query.lng),
            },
          },
        };
      } else {
        // searchParams empty, set my location default
        await this.locateMe();
      }
      await this.submitSearchForm();
    } else if (this.$store.state.search.searchParams != null) {
      // If searchParams exists (not a new user) --> show values
      this.searchParams = this.$store.state.search.searchParams;
      if (this.searchParams.place.formatted_address) {
        this.searchAddress = this.searchParams.place.formatted_address;
      } else {
        this.searchAddress = this.$t('lang.search.my_location');
      }
    }
    const ipCountry = await getCountryFromIp();

    let citiesOfCountry = cities.filter((city) => city.country === ipCountry);
    citiesOfCountry = citiesOfCountry.filter((city) => city.main_city_search === true);

    const list = [];
    citiesOfCountry.forEach((city) => {
      const name = this.$t(`lang.cities.${city.id}`);
      const { geometry } = city;
      list.push({ name, geometry });
    });
    this.citiesList = list;
  },

  methods: {
    writing() {
      const { value } = document.getElementById('locations-input');
      if (value === '') {
        this.showDropdown = true;
      } else {
        this.showDropdown = false;
      }
      this.searchAddress = value;
    },

    focusElement(id) {
      if (id === 'locations-input') {
        if (this.searchAddress !== '') {
          this.searchAddress = '';
          const input = document.getElementById('locations-input');
          if (input) {
            input.value = ''; // empty the real value of the input inside de autocomplete component
          }
        }
        this.showDropdown = true;
      } else {
        this.showDropdown = false;
      }
      document.getElementById(id).focus();
    },
    openDropdown() {
      this.showDropdown = true;
    },

    closeDropdown() {
      // await 2s to prevent error if clicking on one of the cities options (blur executes before click)
      setTimeout(() => {
        if (document.activeElement.id !== 'locations-input' || document.getElementById('locations-input').value !== '') {
          this.showDropdown = false;
        }
      }, '1000');
    },

    async getLocation() {
      return new Promise((resolve, reject) => {
        if (!('geolocation' in navigator)) {
          reject(new Error('Geolocation is not available.'));
        }
        navigator.geolocation.getCurrentPosition((pos) => {
          resolve(pos);
        }, (err) => {
          reject(err);
        });
      });
    },

    async locateMe(from) {
      this.gettingCurrentLocation = true;
      this.currentLocationError = '';
      try {
        // notify apps
        if (window.webkit && window.webkit.messageHandlers.getLocation) {
          window.webkit.messageHandlers.getLocation.postMessage({ action: 'request_location' });
        } else if (window.Android && window.Android.getLocation) {
          window.Android.getLocation(JSON.stringify({ action: 'request_location' }));
        }

        this.location = await this.getLocation();

        this.searchParams.place = {
          geometry: {
            location: {
              lat: this.location.coords.latitude,
              lng: this.location.coords.longitude,
            },
          },
        };
        this.gettingCurrentLocation = false;
        this.searchAddress = this.$t('lang.search.my_location');
      } catch (e) {
        this.gettingCurrentLocation = false;
        if (from !== 'mounted') {
          this.currentLocationError = e.code;
        }
      }
    },

    // Event triggered every time the Location Autocomplete text-box is changed.
    setPlace(place, from) {
      this.showDropdown = false;
      this.currentLocationError = '';
      if (from === 'fromCitiesList') {
        this.searchParams.place = { geometry: place.geometry };
        this.searchParams.place.formatted_address = place.name;
        this.searchAddress = place.name;
        this.showDropdown = false;
      } else {
        this.searchParams.place = place;
        this.searchAddress = place.formatted_address;
      }

      this.errors = {};

      try {
        this.$store.dispatch('setSearchParams', this.searchParams);
      } catch (e) {
        this.errors = { data_error: [this.$t('lang.search.place_error')] };
      }
    },

    updateDates(startDate, endDate) {
      const lang = window.navigator.language;
      let input = '';

      const today = new Date().toLocaleString(lang, { day: '2-digit', month: '2-digit' });
      let tomorrow = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
      tomorrow = tomorrow.toLocaleString(lang, { day: '2-digit', month: '2-digit' });

      const start = startDate.toLocaleString(lang, { day: '2-digit', month: '2-digit' });
      const end = endDate.toLocaleString(lang, { day: '2-digit', month: '2-digit' });

      // Usamos en-US para las horas para que muestre AM o PM. En otros idiomas se muestra feo.
      const startHour = startDate.toLocaleString('en-US', { hour: 'numeric', hour12: true });
      const endHour = endDate.toLocaleString('en-US', { hour: 'numeric', hour12: true });

      if (start === end && start === today) {
        // TODAY
        input = `${this.$t('lang.search.dates.today')} | ${startHour} → ${endHour}`;
      } else if (start === end && start === tomorrow) {
        // TOMORROW
        input = `${this.$t('lang.search.dates.tomorrow')} | ${startHour} → ${endHour}`;
      } else if (start === end) {
        // OTHER DAY, BUT ONLY ONE DAY
        input = `${start} | ${startHour} → ${endHour}`;
      } else {
        // SEVERAL DAYS - RANGE
        input = `${start} ${startHour} → ${end} ${endHour}`;
      }
      this.inputDate = input;

      this.searchParams.input_start_date = startDate;
      this.searchParams.input_end_date = endDate;
    },

    // Submit search form. Validate and API call
    async submitSearchForm(e = null) {
      if (e) {
        e.preventDefault();
      }
      this.errors = {};

      if (this.searchParams.place == null) {
        this.errors = { data_error: [this.$t('lang.search.place_error')] };
        return;
      }

      if (this.searchParams.input_end_date <= this.searchParams.input_start_date) {
        this.errors = { data_error: [this.$t('lang.search.date_error')] };
        return;
      }

      this.$store.dispatch('setSearchParams', this.searchParams);

      if (this.$route.name.includes('Search')) {
        this.$emit('searchFormSubmitted', this.searchParams.place);
      } else {
        this.$router.push(`/${this.$store.state.main.lang}/search`);
      }
    },

    bagsNumber(action) {
      if (action === 'minus') {
        if (this.searchParams.bags_number > 1) {
          this.searchParams.bags_number--;
        }
      } else if (this.searchParams.bags_number < 50) {
        this.searchParams.bags_number++;
      }
    },
  },
};
</script>

<style scoped>

.custom-search-input-2 i{
  font-size: .9rem;
  padding: 0 6px;
}
.locations-dropdown{
  padding: 0;
  border-radius: 3px;
  margin: 0 0 0 10px;

}
.locations-dropdown-text{
  font-size: .7rem!important;
  color: #555!important;
  font-style: italic!important;
  margin: 0!important;
  padding: 0.5rem 1rem!important;
  background-color: #f8f8f8;
  border-radius: 3px 3px 0 0;
}
.dropdown-divider{
  margin: .2rem 0;
}
.dropdown-item{
  font-size: .9rem!important;
  color: #555!important;
  border-top: #e9ecef solid 1px;
  padding: 0.25rem 1rem!important;

}
</style>
